import { useCallback, useEffect, useState } from "react";
import { api } from "@/api/client";
import { useAuth } from "@/auth/AuthContext";
import { toast } from "sonner";
import { formatMoney } from "@/lib/money";
import { EmptyState } from "@/components/EmptyState";
import { StatementReviewModal } from "@/components/StatementReviewModal";
import { EmailConfirmModal } from "@/components/EmailConfirmModal";
import { useEmailGuard } from "@/hooks/useEmailGuard";
import { Receipt, Download, FileSpreadsheet, Loader2, Send, RotateCcw } from "lucide-react";

// Job Ledger — Freelancer Job Assignment Tool Phase 4 (2026-02). Finance-only (`ledger.read`/
// `ledger.write`), a cross-project view of every APPROVED Freelancer Assignment with editable
// payment tracking. Mirrors GET /v1/ledger exactly — filters/sort are all server-side, this
// page never re-derives them client-side.

const dateOnly = (iso) => (iso ? iso.slice(0, 10) : "—");

// Part 8 Cursor review item 6 (2026-10): same mapping StatementReviewModal.jsx uses for its
// own 403 email_send_blocked — kept as a small local duplicate (one call site each) rather
// than a shared util, per the minimal-change scope of this fix.
const emailBlockedReason = (reason) => {
  const r = reason || "";
  if (r.includes("email_sends_disabled")) return "Email sending is currently disabled (EMAIL_SENDS_DISABLED). Ask the founder to enable it in Settings.";
  if (r.includes("email_allowlist")) return "This recipient isn't on the EMAIL_ALLOWLIST. Ask the founder to add them in Settings \u2192 Email Health.";
  if (r.includes("blocked_malformed_recipient")) return "This recipient's email address looks malformed — fix it on the freelancer's profile first.";
  if (r.includes("blocked_non_production")) return "This is a non-production environment — only allowlisted test recipients can receive email here.";
  return `Email send blocked: ${r || "unknown reason"}`;
};

const monthLabel = (m) => {
  if (!m) return "";
  const [y, mo] = m.split("-");
  return new Date(Number(y), Number(mo) - 1, 1).toLocaleDateString("en-US", { month: "long", year: "numeric" });
};

const PAYMENT_PILL = {
  Paid: "bg-emerald-500/10 text-emerald-600",
  Invoiced: "bg-blue-500/10 text-blue-600",
  "On Hold": "bg-red-500/10 text-red-600",
  Pending: "bg-amber-500/10 text-amber-600",
};

function EditableText({ value, onCommit, testId, placeholder, width = "w-28" }) {
  const [local, setLocal] = useState(value || "");
  useEffect(() => setLocal(value || ""), [value]);
  return (
    <input
      data-testid={testId} value={local} placeholder={placeholder}
      onChange={(e) => setLocal(e.target.value)}
      onBlur={() => { if (local !== (value || "")) onCommit(local); }}
      onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
      className={`${width} rounded-md border border-hairline bg-paper px-2 py-1 text-xs focus:border-brand outline-none transition-colors`}
    />
  );
}

export default function Ledger() {
  const { hasScope } = useAuth();
  const canRead = hasScope("ledger.read");
  const canWrite = hasScope("ledger.write");

  const [activeTab, setActiveTab] = useState("ledger");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [showEstimated, setShowEstimated] = useState(false);
  const [exporting, setExporting] = useState(null);

  const [month, setMonth] = useState(undefined); // undefined = let backend pick current month
  const [client, setClient] = useState("");
  const [freelancerId, setFreelancerId] = useState("");
  const [projectId, setProjectId] = useState("");
  const [paymentStatus, setPaymentStatus] = useState("");

  const buildParams = useCallback(() => {
    const params = {};
    if (month !== undefined) params.month = month;
    if (client) params.client = client;
    if (freelancerId) params.freelancer_id = freelancerId;
    if (projectId) params.project_id = projectId;
    if (paymentStatus) params.payment_status = paymentStatus;
    return params;
  }, [month, client, freelancerId, projectId, paymentStatus]);

  const load = useCallback(() => {
    if (!canRead) { setLoading(false); return; }
    setLoading(true);
    api.get("/v1/ledger", { params: buildParams() })
      .then((r) => { setData(r.data); if (month === undefined) setMonth(r.data.selected_month); })
      .finally(() => setLoading(false));
  }, [canRead, buildParams, month]);

  useEffect(load, [load]);

  const patchRow = async (assignmentId, patch) => {
    setSavingId(assignmentId);
    try {
      await api.patch(`/v1/ledger/${assignmentId}`, patch);
      setData((prev) => ({
        ...prev,
        items: prev.items.map((it) => (it.id === assignmentId ? { ...it, ...patch } : it)),
        unpaid_count: prev.items.reduce((acc, it) => {
          const status = it.id === assignmentId ? patch.payment_status ?? it.payment_status : it.payment_status;
          return acc + (status !== "Paid" ? 1 : 0);
        }, 0),
      }));
      toast.success("Ledger entry updated");
    } catch {
      toast.error("Couldn't update — try again");
    } finally {
      setSavingId(null);
    }
  };

  const doExport = async (format) => {
    setExporting(format);
    try {
      const r = await api.get("/v1/ledger/export", { params: { ...buildParams(), format }, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `job_ledger.${format}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Export failed");
    } finally {
      setExporting(null);
    }
  };

  if (!canRead) {
    return <div className="p-8 text-sm text-faint" data-testid="ledger-forbidden">Access Restricted — finance only.</div>;
  }
  if (loading && !data) {
    return <div className="p-8" data-testid="ledger-skeleton"><div className="text-sm text-faint">Loading ledger…</div></div>;
  }

  const items = data?.items || [];
  const opts = data?.filter_options || { months: [], clients: [], freelancers: [], projects: [], payment_statuses: [], gst_rates: [] };

  return (
    <div className="p-5 sm:p-10 space-y-6" data-testid="ledger-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="font-editorial text-2xl sm:text-3xl font-bold uppercase tracking-tight text-ink inline-flex items-center gap-2.5">
            <Receipt className="h-7 w-7 text-brand" /> Job Ledger
          </div>
          <p className="text-sm text-ink-soft mt-1.5">Every Approved Freelancer Assignment, with editable payment tracking. Never shows Production Partner spend.</p>
        </div>
        {canWrite && (
          <div className="flex items-center gap-2">
            <button data-testid="ledger-export-csv-btn" onClick={() => doExport("csv")} disabled={exporting !== null}
              className="inline-flex items-center gap-1.5 rounded-md border border-hairline px-3 py-1.5 text-xs text-ink-soft hover:border-brand hover:text-ink disabled:opacity-50 transition-colors">
              {exporting === "csv" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />} CSV
            </button>
            <button data-testid="ledger-export-xlsx-btn" onClick={() => doExport("xlsx")} disabled={exporting !== null}
              className="inline-flex items-center gap-1.5 rounded-md border border-brand/40 bg-brand/10 px-3 py-1.5 text-xs font-medium text-ink hover:bg-brand/20 disabled:opacity-50 transition-colors">
              {exporting === "xlsx" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileSpreadsheet className="h-3.5 w-3.5" />} Export XLSX
            </button>
          </div>
        )}
      </div>

      <div className="flex items-center gap-1 border-b border-hairline" data-testid="ledger-tabs">
        {[["ledger", "Ledger"], ["statements", "Statements"]].map(([key, label]) => (
          <button key={key} data-testid={`ledger-tab-${key}`} onClick={() => setActiveTab(key)}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${activeTab === key ? "border-brand text-ink" : "border-transparent text-faint hover:text-ink-soft"}`}>
            {label}
          </button>
        ))}
      </div>

      {activeTab === "statements" ? (
        <StatementsTab month={month} setMonth={setMonth} monthOptions={data?.filter_options?.months || []} canWrite={canWrite} />
      ) : (
      <>
      {data && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <Stat label="Records" value={data.total} testId="ledger-total-stat" />
          <Stat label="Unpaid" value={data.unpaid_count} testId="ledger-unpaid-stat" accent={data.unpaid_count > 0} />
          <Stat label="Final Approved Total" value={formatMoney(data.total_final_approved_amount)} testId="ledger-total-amount-stat" />
          <Stat label="Viewing" value={month === "__all__" ? "All months" : monthLabel(month)} testId="ledger-viewing-stat" />
        </div>
      )}

      <div className="rounded-lg border border-hairline bg-paper p-4 sm:p-5 flex items-end gap-4 flex-wrap" data-testid="ledger-filter-bar">
        <Field label="Month">
          <select data-testid="ledger-month-filter" value={month ?? ""} onChange={(e) => setMonth(e.target.value)}
            className="rounded-md border border-hairline bg-paper px-3 py-2 text-sm w-40 focus:border-brand outline-none transition-colors">
            <option value="__all__">All months</option>
            {opts.months.map((m) => <option key={m} value={m}>{monthLabel(m)}</option>)}
          </select>
        </Field>
        <Field label="Client">
          <select data-testid="ledger-client-filter" value={client} onChange={(e) => setClient(e.target.value)}
            className="rounded-md border border-hairline bg-paper px-3 py-2 text-sm w-44 focus:border-brand outline-none transition-colors">
            <option value="">All clients</option>
            {opts.clients.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </Field>
        <Field label="Freelancer">
          <select data-testid="ledger-freelancer-filter" value={freelancerId} onChange={(e) => setFreelancerId(e.target.value)}
            className="rounded-md border border-hairline bg-paper px-3 py-2 text-sm w-44 focus:border-brand outline-none transition-colors">
            <option value="">All freelancers</option>
            {opts.freelancers.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
          </select>
        </Field>
        <Field label="Project">
          <select data-testid="ledger-project-filter" value={projectId} onChange={(e) => setProjectId(e.target.value)}
            className="rounded-md border border-hairline bg-paper px-3 py-2 text-sm w-44 focus:border-brand outline-none transition-colors">
            <option value="">All projects</option>
            {opts.projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </Field>
        <Field label="Payment Status">
          <select data-testid="ledger-payment-status-filter" value={paymentStatus} onChange={(e) => setPaymentStatus(e.target.value)}
            className="rounded-md border border-hairline bg-paper px-3 py-2 text-sm w-40 focus:border-brand outline-none transition-colors">
            <option value="">All statuses</option>
            {opts.payment_statuses.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </Field>
        <label className="flex items-center gap-2 pb-2 text-xs text-ink-soft cursor-pointer">
          <input type="checkbox" data-testid="ledger-show-estimated-toggle" checked={showEstimated} onChange={(e) => setShowEstimated(e.target.checked)} className="h-3.5 w-3.5" />
          Show Estimated Budget
        </label>
      </div>

      <div className="rounded-lg border border-hairline bg-paper overflow-x-auto" data-testid="ledger-table">
        {items.length === 0 ? (
          <EmptyState title="No approved assignments match these filters" hint="Try widening the Month filter to 'All months'." testId="ledger-empty" />
        ) : (
          <table className="w-full text-sm whitespace-nowrap">
            <thead>
              <tr className="border-b border-hairline text-left text-[11px] uppercase tracking-wider text-faint">
                <th className="px-3 py-3 font-medium">Assignment ID</th>
                <th className="px-3 py-3 font-medium">Project</th>
                <th className="px-3 py-3 font-medium">Client</th>
                <th className="px-3 py-3 font-medium">PM</th>
                <th className="px-3 py-3 font-medium">Freelancer</th>
                <th className="px-3 py-3 font-medium">Role</th>
                <th className="px-3 py-3 font-medium">City</th>
                {showEstimated && <th className="px-3 py-3 font-medium text-right">Estimated Budget</th>}
                <th className="px-3 py-3 font-medium text-right">Agreed Budget</th>
                <th className="px-3 py-3 font-medium text-right">Final Charges</th>
                <th className="px-3 py-3 font-medium text-right">Expenses</th>
                <th className="px-3 py-3 font-medium text-right">Final Approved</th>
                <th className="px-3 py-3 font-medium">Completion Date</th>
                <th className="px-3 py-3 font-medium">Approved Date</th>
                <th className="px-3 py-3 font-medium">Payment Status</th>
                <th className="px-3 py-3 font-medium">Payment Date</th>
                <th className="px-3 py-3 font-medium">Invoice No</th>
                <th className="px-3 py-3 font-medium">GST</th>
                <th className="px-3 py-3 font-medium">Finance Notes</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-hairline">
              {items.map((it) => {
                const busy = savingId === it.id;
                return (
                  <tr key={it.id} data-testid={`ledger-row-${it.id}`} className="hover:bg-surface2 transition-colors">
                    <td className="px-3 py-2.5 font-mono text-xs text-ink-soft">{it.human_id}</td>
                    <td className="px-3 py-2.5 text-ink">{it.project_name || "—"}</td>
                    <td className="px-3 py-2.5 text-ink-soft">{it.client_name || "—"}</td>
                    <td className="px-3 py-2.5 text-ink-soft">{it.pm_name || "—"}</td>
                    <td className="px-3 py-2.5 text-ink">{it.freelancer_name || "—"}</td>
                    <td className="px-3 py-2.5 text-ink-soft">{it.role || "—"}</td>
                    <td className="px-3 py-2.5 text-ink-soft">{it.city || "—"}</td>
                    {showEstimated && <td className="px-3 py-2.5 text-right font-mono text-xs text-faint" data-testid={`ledger-estimated-${it.id}`}>{formatMoney(it.estimated_budget)}</td>}
                    <td className="px-3 py-2.5 text-right font-mono text-xs text-ink-soft">{formatMoney(it.agreed_budget)}</td>
                    <td className="px-3 py-2.5 text-right font-mono text-xs text-ink-soft">{formatMoney(it.final_charges)}</td>
                    <td className="px-3 py-2.5 text-right font-mono text-xs text-ink-soft">{formatMoney(it.expenses)}</td>
                    <td className="px-3 py-2.5 text-right font-mono text-xs font-semibold text-ink" data-testid={`ledger-final-approved-${it.id}`}>{formatMoney(it.final_approved_amount)}</td>
                    <td className="px-3 py-2.5 text-xs text-ink-soft">{dateOnly(it.completed_at)}</td>
                    <td className="px-3 py-2.5 text-xs text-ink-soft">{dateOnly(it.approved_date)}</td>
                    <td className="px-3 py-2.5">
                      {canWrite ? (
                        <select data-testid={`ledger-payment-status-${it.id}`} value={it.payment_status} disabled={busy}
                          onChange={(e) => patchRow(it.id, { payment_status: e.target.value })}
                          className={`rounded-full px-2 py-1 text-[11px] font-medium uppercase tracking-wider border-0 outline-none cursor-pointer ${PAYMENT_PILL[it.payment_status] || "bg-hairline text-ink-soft"}`}>
                          {opts.payment_statuses.map((s) => <option key={s} value={s}>{s}</option>)}
                        </select>
                      ) : (
                        <span className={`rounded-full px-2 py-1 text-[11px] font-medium uppercase tracking-wider ${PAYMENT_PILL[it.payment_status] || "bg-hairline text-ink-soft"}`}>{it.payment_status}</span>
                      )}
                    </td>
                    <td className="px-3 py-2.5">
                      {canWrite ? (
                        <input type="date" data-testid={`ledger-payment-date-${it.id}`} value={it.payment_date || ""} disabled={busy}
                          onChange={(e) => patchRow(it.id, { payment_date: e.target.value || null })}
                          className="rounded-md border border-hairline bg-paper px-2 py-1 text-xs w-32 focus:border-brand outline-none transition-colors" />
                      ) : (dateOnly(it.payment_date) || "—")}
                    </td>
                    <td className="px-3 py-2.5">
                      {canWrite ? (
                        <EditableText testId={`ledger-invoice-no-${it.id}`} value={it.invoice_no} placeholder="INV-…" width="w-24"
                          onCommit={(v) => patchRow(it.id, { invoice_no: v || null })} />
                      ) : (it.invoice_no || "—")}
                    </td>
                    <td className="px-3 py-2.5">
                      {canWrite ? (
                        <select data-testid={`ledger-gst-${it.id}`} value={it.gst || "None"} disabled={busy}
                          onChange={(e) => patchRow(it.id, { gst: e.target.value })}
                          className="rounded-md border border-hairline bg-paper px-2 py-1 text-xs w-20 focus:border-brand outline-none transition-colors">
                          {opts.gst_rates.map((g) => <option key={g} value={g}>{g}</option>)}
                        </select>
                      ) : (it.gst || "—")}
                    </td>
                    <td className="px-3 py-2.5">
                      {canWrite ? (
                        <EditableText testId={`ledger-finance-notes-${it.id}`} value={it.finance_notes} placeholder="Notes…" width="w-40"
                          onCommit={(v) => patchRow(it.id, { finance_notes: v || null })} />
                      ) : (it.finance_notes || "—")}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
      </>
      )}
    </div>
  );
}

const Field = ({ label, children }) => (
  <div className="flex flex-col gap-1.5">
    <label className="text-[11px] font-medium uppercase tracking-wider text-faint">{label}</label>
    {children}
  </div>
);

const Stat = ({ label, value, testId, accent }) => (
  <div className="rounded-lg border border-hairline bg-paper p-4" data-testid={testId}>
    <div className="text-[10px] uppercase tracking-wider text-faint">{label}</div>
    <div className={`text-xl mt-1 font-mono ${accent ? "text-red-600" : "text-ink"}`}>{value}</div>
  </div>
);

// Freelancer Job Assignment Tool Phase 6 (2026-02) — Statement Generator, the Ledger's second
// tab (deliberately not a separate nav item, per the founder's explicit call — same finance
// audience, same monthly workflow, `month` state is shared/lifted from the parent <Ledger/>).
// Every draft this creates lands in the "finance" Gmail connection ONLY — see
// modules/statements.py's docstring for why there is no fallback path to "info" if it's not
// connected (this tab surfaces that failure as a plain-language banner, never a silent no-op).
function StatementsTab({ month, setMonth, monthOptions, canWrite }) {
  const [preview, setPreview] = useState(null);
  const [history, setHistory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(null); // "all" | freelancerId | null
  const [resending, setResending] = useState(null); // statementId | null
  const [error, setError] = useState(null);
  const [reviewFreelancerId, setReviewFreelancerId] = useState(null);
  const guard = useEmailGuard();

  const effectiveMonth = month && month !== "__all__" ? month : null;

  const load = useCallback(() => {
    if (!effectiveMonth) { setPreview(null); setHistory(null); setLoading(false); return; }
    setLoading(true);
    Promise.all([
      api.get("/v1/statements/preview", { params: { month: effectiveMonth } }),
      api.get("/v1/statements/history", { params: { month: effectiveMonth } }),
    ]).then(([p, h]) => { setPreview(p.data); setHistory(h.data); }).finally(() => setLoading(false));
  }, [effectiveMonth]);

  useEffect(load, [load]);

  const generate = async (freelancerIds) => {
    setGenerating(freelancerIds === null ? "all" : freelancerIds[0]);
    setError(null);
    try {
      const r = await api.post("/v1/statements/generate", { month: effectiveMonth, freelancer_ids: freelancerIds });
      const { generated, skipped } = r.data;
      if (generated.length) toast.success(`${generated.length} statement draft${generated.length > 1 ? "s" : ""} created in finance@`);
      if (skipped.length) toast.error(`${skipped.length} skipped: ${skipped.map((s) => `${s.freelancer_name} (${s.reason})`).join(", ")}`);
      load();
    } catch (e) {
      if (e?.response?.status === 503) {
        setError(e.response.data?.detail?.reason === "finance_gmail_not_connected" ? "Finance Gmail is not connected — connect it in Administration first." : "Gmail isn't configured.");
      } else {
        toast.error("Couldn't generate statements");
      }
    } finally {
      setGenerating(null);
    }
  };

  const resend = async (statementId, freelancerName) => {
    setResending(statementId);
    setError(null);
    try {
      const r = await api.post("/v1/statements/resend", { statement_id: statementId });
      // Email Send Guard (2026-02): resend is always a single external recipient (the
      // freelancer's own address) — nothing re-sends until the shared "This will email N
      // people" modal is confirmed with a server-issued token.
      if (guard.intercept(r.data, async (typedCount) => {
        await api.post("/v1/statements/resend", { statement_id: statementId, confirmation_token: r.data.confirmation_token, confirmed_count: typedCount });
        toast.success(`Resent ${freelancerName}'s statement (exact original content)`);
        load();
      })) return;
      toast.success(`Resent ${freelancerName}'s statement (exact original content)`);
      load();
    } catch (e) {
      if (e?.response?.status === 503) {
        setError(e.response.data?.detail?.reason === "finance_gmail_not_connected" ? "Finance Gmail is not connected — connect it in Administration first." : "Gmail isn't configured.");
      } else if (e?.response?.status === 403 && e.response.data?.detail?.code === "email_send_blocked") {
        // Part 8 Cursor review item 6 (2026-10): specific blocked reason instead of a
        // generic retry toast — retrying a resend changes nothing until the gate itself does.
        setError(emailBlockedReason(e.response.data.detail.reason));
      } else {
        toast.error("Couldn't resend — try again");
      }
    } finally {
      setResending(null);
    }
  };

  if (!effectiveMonth) {
    return (
      <div className="rounded-lg border border-hairline bg-paper p-4 sm:p-5" data-testid="statements-select-month-prompt">
        <Field label="Month">
          <select data-testid="statements-month-filter" value={month ?? ""} onChange={(e) => setMonth(e.target.value)}
            className="rounded-md border border-hairline bg-paper px-3 py-2 text-sm w-40 focus:border-brand outline-none transition-colors">
            <option value="">Select a month…</option>
            {monthOptions.map((m) => <option key={m} value={m}>{monthLabel(m)}</option>)}
          </select>
        </Field>
        <p className="text-sm text-faint mt-3">Statements are generated per calendar month — pick one above (statements can't be generated across "All months").</p>
      </div>
    );
  }

  if (loading && !preview) {
    return <div className="p-4 text-sm text-faint" data-testid="statements-loading">Loading statements…</div>;
  }

  const freelancers = preview?.freelancers || [];
  const lastSentStatementId = (fid) => {
    const rec = (history?.items || []).find((h) => h.freelancer_id === fid && h.action === "sent");
    return rec?.id;
  };

  return (
    <div className="space-y-6" data-testid="statements-tab">
      <div className="rounded-lg border border-hairline bg-paper p-4 sm:p-5 flex items-end justify-between gap-4 flex-wrap">
        <Field label="Month">
          <select data-testid="statements-month-filter" value={month} onChange={(e) => setMonth(e.target.value)}
            className="rounded-md border border-hairline bg-paper px-3 py-2 text-sm w-40 focus:border-brand outline-none transition-colors">
            {monthOptions.map((m) => <option key={m} value={m}>{monthLabel(m)}</option>)}
          </select>
        </Field>
        {canWrite && (
          <button data-testid="statements-generate-all-btn" onClick={() => generate(null)} disabled={generating !== null || freelancers.length === 0}
            className="inline-flex items-center gap-1.5 rounded-md border border-brand/40 bg-brand/10 px-3 py-1.5 text-xs font-medium text-ink hover:bg-brand/20 disabled:opacity-50 transition-colors">
            {generating === "all" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />} Generate All (Drafts Only)
          </button>
        )}
      </div>

      {error && <div className="rounded-lg border border-red-500/30 bg-red-500/5 p-3 text-sm text-red-600" data-testid="statements-error">{error}</div>}

      <div className="rounded-lg border border-hairline bg-paper overflow-x-auto" data-testid="statements-table">
        {freelancers.length === 0 ? (
          <EmptyState title="No approved jobs for this month" hint="Statements can only be generated for freelancers with at least one Approved job in the selected month." testId="statements-empty" />
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-hairline text-left text-[11px] uppercase tracking-wider text-faint">
                <th className="px-3 py-3 font-medium">Freelancer</th>
                <th className="px-3 py-3 font-medium text-right">Jobs</th>
                <th className="px-3 py-3 font-medium text-right">Total Amount</th>
                <th className="px-3 py-3 font-medium">Status</th>
                {canWrite && <th className="px-3 py-3 font-medium"></th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-hairline">
              {freelancers.map((f) => (
                <tr key={f.freelancer_id} data-testid={`statements-row-${f.freelancer_id}`}>
                  <td className="px-3 py-2.5 text-ink">
                    {f.freelancer_name}
                    {!f.email && <span className="ml-2 text-[10px] uppercase tracking-wider text-red-500" data-testid={`statements-no-email-${f.freelancer_id}`}>No registered email</span>}
                  </td>
                  <td className="px-3 py-2.5 text-right font-mono text-xs text-ink-soft">{f.job_count}</td>
                  <td className="px-3 py-2.5 text-right font-mono text-xs font-semibold text-ink">{formatMoney(f.total_amount)}</td>
                  <td className="px-3 py-2.5 text-xs">
                    {f.last_action === "sent" ? (
                      <span className="text-emerald-600" data-testid={`statements-sent-${f.freelancer_id}`}>Sent {dateOnly(f.last_action_at)}</span>
                    ) : f.last_action === "draft" ? (
                      <span className="text-amber-600" data-testid={`statements-generated-${f.freelancer_id}`}>Draft saved {dateOnly(f.last_action_at)}</span>
                    ) : (
                      <span className="text-faint">Not yet generated</span>
                    )}
                  </td>
                  {canWrite && (
                    <td className="px-3 py-2.5 text-right">
                      <div className="inline-flex items-center gap-1.5">
                        {f.last_action === "sent" && (
                          <button data-testid={`statements-resend-${f.freelancer_id}`} onClick={() => resend(lastSentStatementId(f.freelancer_id), f.freelancer_name)}
                            disabled={resending !== null || !lastSentStatementId(f.freelancer_id)}
                            className="inline-flex items-center gap-1 rounded-md border border-hairline px-2.5 py-1 text-xs text-ink-soft hover:border-brand hover:text-ink disabled:opacity-50 transition-colors">
                            {resending === lastSentStatementId(f.freelancer_id) ? <Loader2 className="h-3 w-3 animate-spin" /> : <RotateCcw className="h-3 w-3" />} Resend
                          </button>
                        )}
                        <button data-testid={`statements-review-${f.freelancer_id}`} onClick={() => setReviewFreelancerId(f.freelancer_id)}
                          disabled={!f.email}
                          className="inline-flex items-center gap-1 rounded-md border border-brand/40 bg-brand/10 px-2.5 py-1 text-xs font-medium text-ink hover:bg-brand/20 disabled:opacity-50 transition-colors">
                          Review{f.last_action ? " (new)" : ""}
                        </button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="rounded-lg border border-hairline bg-paper" data-testid="statements-history">
        <div className="border-b border-hairline px-4 py-3 text-[11px] uppercase tracking-wider text-faint">Generation History — {monthLabel(effectiveMonth)}</div>
        {(!history?.items || history.items.length === 0) ? (
          <div className="p-4 text-sm text-faint" data-testid="statements-history-empty">No statements generated yet for this month.</div>
        ) : (
          <table className="w-full text-sm">
            <tbody className="divide-y divide-hairline">
              {history.items.map((h) => (
                <tr key={h.id} data-testid={`statements-history-row-${h.id}`}>
                  <td className="px-4 py-2.5 text-ink">{h.freelancer_name}</td>
                  <td className="px-4 py-2.5 text-right font-mono text-xs text-ink-soft">{formatMoney(h.total_amount)}</td>
                  <td className="px-4 py-2.5 text-xs">
                    {h.action === "sent" ? (
                      <span className="text-emerald-600" data-testid={`statements-history-sent-badge-${h.id}`}>Sent{h.resend_log?.length ? ` (resent ×${h.resend_log.length})` : ""}</span>
                    ) : (
                      <span className="text-amber-600">Draft</span>
                    )}
                  </td>
                  <td className="px-4 py-2.5 text-xs text-ink-soft">{h.action === "sent" ? h.sent_by : h.generated_by}</td>
                  <td className="px-4 py-2.5 text-xs text-faint">{dateOnly(h.action === "sent" ? h.sent_at : h.generated_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {reviewFreelancerId && (
        <StatementReviewModal
          month={effectiveMonth} monthLabel={monthLabel(effectiveMonth)} freelancerId={reviewFreelancerId}
          onClose={() => setReviewFreelancerId(null)} onDone={load}
        />
      )}
      <EmailConfirmModal open={guard.open} preview={guard.preview} busy={guard.busy} onConfirm={guard.confirm} onCancel={guard.cancel} />
    </div>
  );
}
